import React, { useState } from "react";
import LmsTable from "../ui/LmsTable";
import { useParseRouteActions, useFetchData } from "../../../helpers/GlobalHelper";
import LmsFormGenerator, { BCFormController } from "../ui/LmsFormGenerator";
import { GlobalConstants } from "../../constants/GlobalConstants";
import BCModal from "../../ui/BCModal";
import { EditLoanTransactionsForm } from "../Accounting/LmsAccountingLoanTranscations";
import { EditLoanPaymentsForm } from "../Accounting/LmsAccountingLoanPayments";

export const LoanTransactionHistoriesTable = ({ loanId, fieldProps = undefined as any, isDisabled = false }) => {
  const [modalData, setModalData] = useState<{
    transactionId: string;
    chargePayment: "Charge" | "Payment";
  } | null>(null);

  const handleRowClick = ({ clickedRow: { hasActionLink, chargePayment, id }}) => {
    if (hasActionLink) {
      setModalData({ chargePayment, transactionId: id });
    }
  };

  const handleModalClose = () => {
    setModalData(null);
  };

  return (
    <>
      <LmsTable
        isDisabled={isDisabled}
        skipCheckbox
        skipCheckboxAll
        hideFilters
        hideFilterBar
        hideProps={[
          "active", 
          "hasActionLink", 
          "bottomBorder", 
          "color", 
          ...(fieldProps?.inputs?.includeAllocatedCosts 
            ? [] 
            : ["allocatedPerActiveUnit", "allocatedCostPerActiveUnit", "unitsSplit", "properties", "color", "bottomBorder"]
          )
        ]}
        rowsBackgroundCustomColor={(row) => (row.active === "No" ? "pink" : "")}
        columnClickOptions={{
          chargePayment: {
            path: "",
            idProp: "id",
          },
        }}
        columnLabelsDictionary={{
          transactionCategory: "Trans Cat",
          transactionType: "Trans Type",
          chargePayment: "Charge/Pmt",
          expectPayment: "Expect Pmt",
          addToLoanBalance: "Add To Ln Bal",
          transEffectiveDate: "Trans Eff Date",
          checkOrWire: "C/W",
          transactionAmount: "Trans Amount",
          interestRateHistory: "Int Rate Hist",
          allocatedPerActiveUnit: "Allocated  % per active unit",
          unitsSplit: "# Units Split"
        }}
        apiSuffix={loanId ? `loandetails/history/transaction-histories/${loanId}` : ""}
        refreshDataDependency={fieldProps?.inputs?.allocatedCosts}
        handleClick={handleRowClick}
        dataRules={[
          { field: "allocatedPerActiveUnit", rule: "format", display: "percent" },
          { field: "interestRateHistory", rule: "format", display: "percent" },
          { field: "allocatedCostPerActiveUnit", rule: "format", display: "dollar" }
        ]}
      />
      <BCModal
        closeIcon
        size="fullscreen"
        isModalOpen={!!modalData}
        onClose={handleModalClose}
        content={
          modalData &&
          (modalData.chargePayment === "Charge" ? (
            <EditLoanTransactionsForm
              handleClose={handleModalClose}
              loanTransactionId={modalData.transactionId}
            />
          ) : (
            <EditLoanPaymentsForm id={modalData.transactionId} handleClose={handleModalClose} />
          ))
        }
      />
    </>
  );
};

const editLoanHistoryFields = (form: BCFormController, loanId = "", isInterestRate = false): BCFormController => {
  form.addGroupFields([
    form.getDisplayField("Borrower Name", { name: "borrowerOrganization" }),
    form.getDisplayField("SPE Name", { name: "specialPurposeEntity" }),
    form.getDisplayField("Project Name"),
    form.getDisplayField("Loan #", { name: "loanNumber" }),
    form.getCurrencyField("Current Balance", {
      disabled: true,
      name: "loanAmountCurrent",
    }),
  ]);

  if (isInterestRate) {
    form.addComponent(<LmsTable apiSuffix={`loans/${loanId}/interest-rate-histories`} isDisabled />);
  }
  else {
    form.addComponent(<LoanTransactionHistoriesTable loanId={loanId} />);
  }

  return form;
};

const LmsServicingLoanHistoryEditPage = ({ id, isInterestRate = false }) => {
  const [{ data }] = useFetchData(`loans/${id}/details`);
  const myForm = editLoanHistoryFields(new BCFormController(data), id, isInterestRate);
  let actions: any[];
  if (isInterestRate) {
    actions = [
      "close",
      {
        text: "Excel",
        action: "download",
        path: `loans/${id}/interest-rate-histories/export`,
        icon: GlobalConstants.Icons.EXCEL_OUTLINE,
        method: "POST",
      },
    ];
  }
  else {
    actions = [
      {
        text: "Borrower Friendly Export History",
        action: "download",
        path: `loandetails/history/borrower-friendly-excel/${id}`,
        icon: GlobalConstants.Icons.EXCEL_OUTLINE,
      },
      {
        text: "Internal Friendly Export History",
        action: "download",
        path: `loandetails/history/internal-friendly-excel/${id}`,
        icon: GlobalConstants.Icons.EXCEL_OUTLINE,
      },
      "close",
    ];
  }

  return <LmsFormGenerator myForm={myForm} actions={actions} />;
};

const LmsServicingLoanHistoryMainPage = () => {
  const editColumnLinkProperties = {
    path: "/servicing-loan-history/edit/{id}",
    idProp: "id",
  };

  const isExportDisabled = (checkedItemsI: number[]): boolean => checkedItemsI.length !== 1;

  return (
    <LmsTable
      rowsBackgroundCustomColor={(row) => row.frozen === "Yes" ? "cyan" : ""}
      apiSuffix="/loandetails/history"
      columnLabelsDictionary={{
        borrowerOrganization: "Borrower",
        applicationNumber: "Application #",
        loanNumber: "Loan #",
      }}
      columnClickOptions={{
        applicationNumber: editColumnLinkProperties,
        transactionHistory: editColumnLinkProperties,
        interestRateHistory: {
          path: "/servicing-loan-history/interest-rate/{id}",
          idProp: "id",
        },
      }}
      actionButtons={[
        "edit",
        "export",
        {
          text: "Borrower Friendly Export History",
          isDisabled: isExportDisabled,
          action: "downloadselected",
          downloadSuffix: "loandetails/history/borrower-friendly-excel"
        },
        {
          text: "Internal Friendly Export History",
          isDisabled: isExportDisabled,
          action: "downloadselected",
          downloadSuffix: "loandetails/history/internal-friendly-excel"
        },
      ]}
    />
  );
};

const LmsServicingLoanHistory = ({ params = {}, history }) => {
  const { isMainPage, isActionAllowed, isEdit, actionLower, id } = useParseRouteActions(
    params,
    ["edit"],
    ["interest-rate"]
  );

  if (!isActionAllowed) {
    history.goBack();
  }

  return (
    <>
      {isMainPage && <LmsServicingLoanHistoryMainPage />}
      {isEdit && (
        <LmsServicingLoanHistoryEditPage id={id} />
      )}
      {actionLower === "interest-rate" && (
        <LmsServicingLoanHistoryEditPage id={id} isInterestRate={true} />
      )}
    </>
  );
};

export default LmsServicingLoanHistory;
